import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "../lib/api";
import { track } from "../lib/analytics";
import { lifeSpan } from "../lib/format";
import { ENV_KEYS } from "../lib/constants";
import { useSite } from "../context/SiteContext";
import { EnvCard } from "../components/EnvCard";
import { MemorialFields, validateFields } from "../components/MemorialFields";
import { PhotoPicker } from "../components/PhotoPicker";
import { MemoriesPicker } from "../components/MemoriesPicker";
import { ModelPicker } from "../components/ModelPicker";
import { StonePreview } from "../scene/StonePreview";
import { Checkbox } from "../components/ui/checkbox";

const STEPS = ["env", "details", "stone", "preview", "place"];

const Stepper = ({ step }) => {
  const { t } = useTranslation();
  return (
    <ol className="flex items-center gap-1.5 sm:gap-4 max-w-full overflow-x-auto pb-2" aria-label={t("wizard.title")} data-testid="wizard-stepper">
      {STEPS.map((s, i) => (
        <li key={s} className="flex items-center gap-1.5 sm:gap-3 shrink-0" aria-current={i === step ? "step" : undefined}>
          <span className={`h-8 w-8 rounded-full grid place-items-center text-xs font-bold transition-colors ${i < step ? "bg-[var(--gold)] text-[var(--dusk)]" : i === step ? "bg-[var(--dusk)] text-[var(--cream)]" : "border text-[hsl(var(--muted-foreground))]"}`}>{i + 1}</span>
          <span className={`text-sm ${i === step ? "font-semibold" : "text-[hsl(var(--muted-foreground))] hidden sm:inline"}`}>{t(`wizard.steps.${s}`)}</span>
          {i < STEPS.length - 1 && <span className="w-3 sm:w-10 h-px bg-[hsl(var(--border))]" aria-hidden="true" />}
        </li>
      ))}
    </ol>
  );
};

const Choice = ({ checked, onChange, title, desc, testId }) => (
  <label className={`flex gap-4 rounded-2xl border p-5 cursor-pointer transition-colors ${checked ? "border-[var(--gold)] bg-[hsl(var(--card))]" : "hover:border-[hsl(var(--accent))]"}`} data-testid={testId}>
    <input type="radio" className="mt-1 accent-[#c9a46a]" checked={checked} onChange={onChange} />
    <span>
      <span className="block font-semibold">{title}</span>
      <span className="block text-sm text-[hsl(var(--muted-foreground))] mt-1">{desc}</span>
    </span>
  </label>
);

const Agree = ({ checked, onChange, children, testId }) => (
  <label className="flex gap-3 items-start text-sm cursor-pointer">
    <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" data-testid={testId} />
    <span>{children}</span>
  </label>
);

export default function CreateWizard() {
  const { t } = useTranslation();
  const { lang, settings } = useSite();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [envs, setEnvs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState({ environment: "", name: "", surname: "", species: "dog", breed: "", birth_date: "", death_date: "", epitaph: "", epitaph_font: "classic", model: "classic", visibility: "unlisted", consent_public: false, accept_terms: false, email: "", website: "" });
  const [photo, setPhoto] = useState(null);
  const [memories, setMemories] = useState([]);
  const [captions, setCaptions] = useState([]);
  const preview = useMemo(() => (photo ? URL.createObjectURL(photo) : null), [photo]);
  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  useEffect(() => {
    track("wizard_start");
    api.get("/environments").then(({ data: d }) => setEnvs(d)).catch(() => {});
  }, []);
  useEffect(() => {
    if (step > 0 && step < 4) track("wizard_step", { step: step + 1 });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const stoneData = { ...data, slug: "preview", photoUrl: preview, flowers_count: 0 };
  const next = () => {
    if (step === 0 && !data.environment) return toast.error(t("errors.invalid_environment"));
    if (step === 1) {
      const err = validateFields(data);
      if (err) return toast.error(t(`errors.${err}`));
    }
    setStep((s) => s + 1);
  };

  const submit = async () => {
    if (!data.accept_terms) return toast.error(t("errors.terms_required"));
    if (data.visibility === "public" && !data.consent_public) return toast.error(t("errors.consent_required"));
    setBusy(true);
    setStep(4);
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? String(v) : v));
    fd.append("lang", lang);
    if (photo) fd.append("photo", photo);
    memories.forEach((f, i) => {
      fd.append("gallery", f);
      fd.append("gallery_captions", captions[i] || "");
    });
    try {
      const { data: res } = await api.post("/memorials", fd);
      track("wizard_complete");
      navigate(`/explore/${res.environment}?m=${res.slug}&arrive=1`, { state: { manageToken: res.manage_token } });
    } catch (e) {
      toast.error(errorText(t, e));
      setStep(3);
      setBusy(false);
    }
  };

  if (settings.features?.creation === false) {
    return (
      <main id="main" className="mx-auto max-w-3xl px-5 pt-40 pb-24 text-center" data-testid="creation-disabled">
        <h1 className="font-heading text-4xl">{t("wizard.title")}</h1>
        <p className="mt-6 text-[hsl(var(--muted-foreground))]">{t("errors.creation_disabled")}</p>
      </main>
    );
  }

  return (
    <main id="main" className="mx-auto max-w-6xl px-5 sm:px-8 pt-32 pb-24" data-testid="create-wizard">
      <p className="eyebrow">{t("wizard.title")}</p>
      <div className="mt-6 mb-12"><Stepper step={step} /></div>

      {step === 0 && (
        <section className="anim-rise" data-testid="wizard-step-env">
          <h1 className="font-heading text-3xl sm:text-4xl">{t("wizard.step1_title")}</h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-3">{t("wizard.step1_lead")}</p>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {ENV_KEYS.map((k) => (
              <EnvCard key={k} env={k} size="lg" info={envs.find((e) => e.key === k)} selected={data.environment === k} onSelect={(env) => setData({ ...data, environment: env })} testId={`wizard-env-${k}`} />
            ))}
          </div>
        </section>
      )}

      {step === 1 && (
        <section className="anim-rise max-w-3xl" data-testid="wizard-step-details">
          <h1 className="font-heading text-3xl sm:text-4xl">{t("wizard.step2_title")}</h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-3 mb-10">{t("wizard.step2_lead")}</p>
          <div className="grid gap-8">
            <div>
              <p className="field-label">{t("wizard.photo")}</p>
              <PhotoPicker preview={preview} onFile={setPhoto} onRemove={() => setPhoto(null)} testId="wizard-photo" />
            </div>
            <MemoriesPicker files={memories} captions={captions} onChange={(f, c) => { setMemories(f); setCaptions(c); }} />
            <MemorialFields value={data} onChange={setData} prefix="wizard" />
            <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-label={t("wizard.honeypot")} className="hidden" value={data.website} onChange={(e) => setData({ ...data, website: e.target.value })} />
          </div>
        </section>
      )}

      {step === 2 && (
        <section className="anim-rise grid lg:grid-cols-2 gap-10 items-start" data-testid="wizard-step-stone">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl">{t("wizard.step3_title")}</h1>
            <p className="text-[hsl(var(--muted-foreground))] mt-3 mb-8">{t("wizard.step3_lead")}</p>
            <ModelPicker value={data.model} onChange={(m) => setData({ ...data, model: m })} prefix="wizard-model" />
          </div>
          <StonePreview memorial={stoneData} env={data.environment} testId="wizard-stone-preview" />
        </section>
      )}

      {(step === 3 || step === 4) && (
        <section className="anim-rise grid lg:grid-cols-2 gap-10 items-start" data-testid="wizard-step-preview">
          <div className="lg:sticky lg:top-28">
            <StonePreview memorial={stoneData} env={data.environment} testId="wizard-final-preview" height="h-[460px]" />
            <div className="flex flex-wrap gap-2 mt-4">
              <button className="btn-ghost !py-2" onClick={() => setStep(1)} data-testid="preview-edit-details-btn"><Pencil size={14} /> {t("wizard.edit_details")}</button>
              <button className="btn-ghost !py-2" onClick={() => setStep(2)} data-testid="preview-edit-stone-btn"><Pencil size={14} /> {t("wizard.edit_stone")}</button>
              <button className="btn-ghost !py-2" onClick={() => setStep(0)} data-testid="preview-edit-env-btn"><Pencil size={14} /> {t("wizard.edit_env")}</button>
            </div>
          </div>
          <div className="space-y-8">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl">{t("wizard.step4_title")}</h1>
              <p className="text-[hsl(var(--muted-foreground))] mt-3">{t("wizard.step4_lead")}</p>
            </div>
            <dl className="rounded-3xl bg-[hsl(var(--card))] border p-6 grid gap-3 text-sm" data-testid="preview-summary">
              <div className="flex justify-between gap-4"><dt className="text-[hsl(var(--muted-foreground))]">{t("wizard.steps.env")}</dt><dd className="font-semibold">{t(`env.${data.environment}.name`)}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-[hsl(var(--muted-foreground))]">{t("wizard.name")}</dt><dd className="font-semibold" dir="auto">{data.name} {data.surname}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-[hsl(var(--muted-foreground))]">{t("wizard.species")}</dt><dd>{t(`species.${data.species}`)}{data.breed && ` · ${data.breed}`}</dd></div>
              {lifeSpan(data) && <div className="flex justify-between gap-4"><dt className="text-[hsl(var(--muted-foreground))]">{t("wizard.death_date")}</dt><dd>{lifeSpan(data)}</dd></div>}
              <div className="flex justify-between gap-4"><dt className="text-[hsl(var(--muted-foreground))]">{t("wizard.steps.stone")}</dt><dd>{t(`models.${data.model}.name`)}</dd></div>
            </dl>
            <fieldset className="space-y-3">
              <legend className="field-label">{t("wizard.visibility_title")}</legend>
              <Choice checked={data.visibility === "unlisted"} onChange={() => setData({ ...data, visibility: "unlisted", consent_public: false })} title={t("wizard.visibility_unlisted")} desc={t("wizard.visibility_unlisted_desc")} testId="visibility-unlisted" />
              <Choice checked={data.visibility === "public"} onChange={() => setData({ ...data, visibility: "public" })} title={t("wizard.visibility_public")} desc={t("wizard.visibility_public_desc")} testId="visibility-public" />
              {data.visibility === "public" && (
                <Agree checked={data.consent_public} onChange={(v) => setData({ ...data, consent_public: v })} testId="consent-public-checkbox">{t("wizard.consent_public")}</Agree>
              )}
            </fieldset>
            {settings.features?.email_recovery !== false && (
              <div>
                <label htmlFor="wizard-email" className="field-label">{t("wizard.email_title")} <span className="font-normal text-[hsl(var(--muted-foreground))]">({t("common.optional")})</span></label>
                <input id="wizard-email" type="email" className="field-input" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} placeholder={t("wizard.email_label")} data-testid="wizard-email-input" />
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1.5">{t("wizard.email_hint")}</p>
              </div>
            )}
            <Agree checked={data.accept_terms} onChange={(v) => setData({ ...data, accept_terms: v })} testId="accept-terms-checkbox">
              {t("wizard.terms")} <Link to="/terms" target="_blank" className="underline">{t("wizard.terms_link")}</Link> · <Link to="/privacy" target="_blank" className="underline">{t("wizard.privacy_link")}</Link>
            </Agree>
            <button className="btn-gold w-full !py-4 text-base" onClick={submit} disabled={busy} data-testid="wizard-confirm-btn">
              {busy ? <><Loader2 size={18} className="animate-spin" /> {t("wizard.placing")}</> : t("wizard.confirm")}
            </button>
          </div>
        </section>
      )}

      {step < 3 && (
        <div className="flex justify-between mt-14">
          {step > 0 ? <button className="btn-ghost" onClick={() => setStep(step - 1)} data-testid="wizard-back-btn"><ArrowLeft size={16} className="flip-rtl" /> {t("common.back")}</button> : <span />}
          <button className="btn-primary" onClick={next} data-testid="wizard-next-btn">{t("common.next")} <ArrowRight size={16} className="flip-rtl" /></button>
        </div>
      )}
    </main>
  );
}
